/*
 * Sketch to Slide (AI) panel.
 *
 * Select a hand-drawn sketch/wireframe image on the slide, then run the
 * conversion — AI reads the sketch's structure and the result lands as
 * native, editable shapes on a new slide appended to the deck. Mirrors
 * PhotoHeadshotPanel.tsx's structure (single-action run/cancel/reset state
 * machine with a phased progress list) without the design gallery, since
 * this feature has only one outcome per sketch.
 */

import { useCallback } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { RunStatusSection } from "../../shared/components/RunStatusSection";
import { SelectionHint } from "../../shared/components/SelectionHint";
import { useSketchToSlide } from "../hooks/useSketchToSlide";
import { getErrorMessage, logSketchToSlideError } from "../services/sketchToSlideApi";
import type { SketchToSlidePhase, SketchToSlideSummary } from "../../../types";
import shared from "../../shared/styles/panelStatus.module.css";
import styles from "./SketchToSlidePanel.module.css";

interface SketchToSlidePanelProps {
  readonly onBack?: () => void;
}

const PHASES: ReadonlyArray<{ readonly phase: SketchToSlidePhase; readonly label: string }> = [
  { phase: "reading", label: "Reading the selected sketch" },
  { phase: "uploading", label: "Uploading the sketch" },
  { phase: "analyzing", label: "Analyzing the sketch (AI)" },
  { phase: "composing", label: "Building the slide" },
];

function reportRunFailure(error: unknown): void {
  logSketchToSlideError("sketch-to-slide-run-ui", getErrorMessage(error, "Unexpected error"));
}

function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function formatSlideSummary(summary: SketchToSlideSummary): string {
  const shapeSuffix = summary.shapeCount === 1 ? "" : "s";
  const shapeLabel = `${summary.shapeCount} shape${shapeSuffix}`;

  const connectorSuffix = summary.connectorCount === 1 ? "" : "s";
  const connectorLabel =
    summary.connectorCount > 0 ? `, ${summary.connectorCount} connector${connectorSuffix}` : "";

  return `New slide created — ${shapeLabel}${connectorLabel} (${summary.layoutFamily} layout, ${formatConfidence(summary.confidence)} confidence).`;
}

export function SketchToSlidePanel({ onBack }: Readonly<SketchToSlidePanelProps>) {
  const { state, selectedShape, selectionError, run, cancel, reset } = useSketchToSlide();

  const handleRun = useCallback(() => {
    run().catch(reportRunFailure);
  }, [run]);

  return (
    <div className={shared.panel}>
      <ModuleHeader title="Sketch to Slide" subtitle="Deckorate · AI" onBack={onBack} />
      <div className={shared.body}>
        <p className={shared.desc}>
          Select a photo of a hand-drawn sketch or wireframe on the slide, then convert it — AI reads its structure
          and builds a new slide from editable native shapes.
        </p>

        {!selectedShape && (
          <SelectionHint
            selectionError={selectionError}
            defaultMessage="Select a sketch image on the slide to convert it."
            classNames={shared}
          />
        )}

        {selectedShape && state.status === "idle" && (
          <button type="button" className={styles.runBtn} onClick={handleRun}>
            Convert to slide
          </button>
        )}

        <RunStatusSection
          status={state.status}
          phase={state.status === "running" ? state.phase : undefined}
          phases={PHASES}
          progressAriaLabel="Sketch to slide progress"
          doneMessage={state.status === "done" ? formatSlideSummary(state.summary) : undefined}
          errorMessage={state.status === "error" ? state.message : undefined}
          onCancel={cancel}
          onDismiss={reset}
          classNames={shared}
        />
      </div>
    </div>
  );
}
